import { FC, useEffect, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { useNavigate } from "react-router-dom";
import { MuiChipsInput } from "mui-chips-input";
import {
  Alert,
  Box,
  FormControl,
  FormHelperText,
  MenuItem,
  Select,
  Snackbar,
  TextField,
} from "@mui/material";
import { AxiosError } from "axios";
import { useMutation } from "react-query";
import SaveIcon from "@mui/icons-material/Save";
import CancelIcon from "@mui/icons-material/Cancel";
import { Asterisk } from "../../common/components/Asterisk";
import { SectionTitle } from "../../common/components/SectionTitle";
import { ButtonCancelA } from "../../common/components/ButtonCancelA";
import { ButtonSubmitA } from "../../common/components/ButtonSubmitA";
import { fetchCreateCompany } from "../miscellaneous/fetchCreateCompany";
import { fetchParentCompanyLookup } from "../miscellaneous/fetchParentCompanyLookup";

const formControlStyle = {
  marginTop: "20px",
};

const btnGroupStyle = {
  display: "flex",
  flexDirection: "row",
  justifyContent: "center",
  gap: "20px",
  marginTop: "60px",
};

export const CreateNewCompany: FC = () => {
  const {
    control,
    register,
    handleSubmit,
    formState: { errors },
    setValue,
    getValues,
    clearErrors,
  } = useForm();
  const navigate = useNavigate();
  const [chips, setChips] = useState([]);
  const [parentCompanies, setParentCompanies] = useState<any[]>([]);
  const [errorMessage, setErrorMessage] = useState("");
  const [isShowToastError, setIsShowToastError] = useState(false);

  const handleCloseToastError = (
    event?: React.SyntheticEvent | Event,
    reason?: string
  ) => {
    if (reason === "clickaway") {
      return;
    }
    setIsShowToastError(false);
  };

  const userRole = !!localStorage.getItem("rolesJson")
    ? JSON.parse(localStorage.getItem("rolesJson") as string)
    : [];
  const isRoleSupperAdmin =
    userRole &&
    userRole.length > 0 &&
    userRole[0]?.Name &&
    userRole[0]?.Name.toLowerCase() === "super admin";

  const mutateParentCompanyLookup = useMutation(fetchParentCompanyLookup, {
    onSuccess: (res) => {
      if (res.data && res.data.length > 0) {
        setParentCompanies(res.data);
        if (!isRoleSupperAdmin) {
          setValue("parentCompanyId", res.data[0].id);
        }
      }
    },
    onError: () => {},
  });

  useEffect(() => {
    mutateParentCompanyLookup.mutate();
  }, []);

  const mutateCreateCompany = useMutation(fetchCreateCompany, {
    onSuccess: (res) => {
      if (res.status === 200) {
        navigate("/company-management?tab=organization");
      }
    },
    onError: (error: AxiosError) => {
      if (error?.response?.status === 400 && error?.response?.data) {
        const raw = error.response.data as string;
        const message = raw === "Company name is duplicated"
          ? "Organization name is duplicated"
          : raw;
        setErrorMessage(message);
        setIsShowToastError(true);
      }
    },
  });

  const onSubmit = (data: any) => {
    setErrorMessage("");
    const payload = {
      companyName: data.companyName ?? "",
      jobTitles: chips ?? [],
      isActive: data.status === "active" ? true : false,
      parentCompanyId: data.parentCompanyId,
    };

    mutateCreateCompany.mutate(payload);
  };

  const handleChange = (newChips: any) => {
    setChips(newChips);
  };

  const handleBackToCompanies = () => {
    navigate("/company-management?tab=organization");
  };

  return (
    <div className="mt-9">
      <SectionTitle name="Create New Organization" />
      <Box sx={{ minWidth: 120, marginTop: "60px" }}>
        <form className="pr-24 pl-24 pb-24" onSubmit={handleSubmit(onSubmit)}>
          <FormControl fullWidth sx={formControlStyle}>
            <label className="font-bold">
              Company <Asterisk />
            </label>
            <Controller
              control={control}
              name="parentCompanyId"
              defaultValue={""}
              rules={{ required: "Company is required" }}
              render={({ field: { onChange, onBlur, value } }) => (
                <Select
                  id="parentCompanyId"
                  value={value ?? ""}
                  onChange={(e) => {
                    setValue("parentCompanyId", e.target.value);
                    clearErrors("parentCompanyId");
                  }}
                  style={{ height: "38px" }}
                >
                  {parentCompanies.map((pc: any) => (
                    <MenuItem key={pc.id} value={pc.id}>
                      {pc.companyName}
                    </MenuItem>
                  ))}
                </Select>
              )}
            ></Controller>
            <FormHelperText sx={{ color: "red" }}>
              {control.getFieldState("parentCompanyId").error?.message}
            </FormHelperText>
          </FormControl>

          <FormControl fullWidth sx={formControlStyle}>
            <label className="font-bold">
              Organization <Asterisk />
            </label>
            <TextField
              {...register("companyName", {
                required: "Organization is required",
              })}
              defaultValue={""}
              sx={{
                "& .MuiInputBase-input": {
                  height: "5px",
                },
              }}
            />
            <FormHelperText sx={{ color: "red" }}>
              {control.getFieldState("companyName").error?.message}
            </FormHelperText>
          </FormControl>

          <FormControl fullWidth sx={formControlStyle}>
            <label className="font-bold">Job Title</label>
            <MuiChipsInput value={chips} onChange={handleChange} />
          </FormControl>

          <FormControl fullWidth sx={formControlStyle}>
            <label className="font-bold">Status</label>
            <Controller
              control={control}
              name="status"
              defaultValue={"active"}
              rules={{ required: "Status is required" }}
              render={({ field: { onChange, onBlur, value } }) => (
                <Select
                  id="status"
                  value={value ?? "active"}
                  onChange={(e) => {
                    setValue("status", e.target.value);
                    clearErrors("status");
                  }}
                  style={{ height: "38px" }}
                >
                  <MenuItem value={"active"}>Active</MenuItem>
                  <MenuItem value={"inactive"}>Inactive</MenuItem>
                </Select>
              )}
            ></Controller>
            <FormHelperText sx={{ color: "red" }}>
              {control.getFieldState("status").error?.message}
            </FormHelperText>
          </FormControl>

          <FormControl fullWidth sx={btnGroupStyle}>
            <ButtonCancelA
              name="Cancel"
              onHandleClick={handleBackToCompanies}
              icon={<CancelIcon sx={{ marginRight: "4px" }} />}
            />
            <ButtonSubmitA
              name="Save"
              icon={<SaveIcon sx={{ marginRight: "4px" }} />}
            />
          </FormControl>
        </form>
      </Box>
      <Snackbar
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
        open={isShowToastError}
        autoHideDuration={5000}
        onClose={handleCloseToastError}
      >
        <Alert
          onClose={handleCloseToastError}
          severity="error"
          variant="filled"
          sx={{ width: "100%" }}
        >
          <p>{errorMessage}</p>
        </Alert>
      </Snackbar>
    </div>
  );
};
